import { Injectable } from '@angular/core';
import { MyAuthService } from './auth.service';

export const AUTH_ERROR_MESSAGES = [
  'Missing Refresh Token',
  'login_required',
  'consent_required',
  'interaction_required',
  'Unknown or invalid refresh token.',
];

const REDIRECT_COOLDOWN_MS = 5_000;

/**
 * Centralizes the logic for detecting Auth0 session-expiry errors and
 * triggering a login redirect.  A short cooldown prevents duplicate
 * redirects when multiple concurrent requests fail at the same time.
 */
@Injectable({ providedIn: 'root' })
export class AuthRedirectService {
  private redirecting = false;

  constructor(private readonly authService: MyAuthService) {}

  isAuthError(error: unknown): boolean {
    if (!error) return false;
    // HTTP 401 from backend
    if ((error as { status: number }).status === 401) return true;
    // Auth0 SDK errors surfaced before the request reaches the backend
    const message = this.extractMessage(error);
    return AUTH_ERROR_MESSAGES.some((msg) => message.includes(msg));
  }

  redirectToLogin(): void {
    if (this.redirecting) return;
    this.redirecting = true;
    // Reset after cooldown in case the redirect doesn't navigate away
    // (e.g. a popup flow or an unexpected Auth0 error)
    setTimeout(() => (this.redirecting = false), REDIRECT_COOLDOWN_MS);
    this.authService.loginWithAuth0();
  }

  private extractMessage(error: unknown): string {
    if (error instanceof Error) return error.message;
    if (typeof error === 'string') return error;
    // Auth0 sometimes nests the real error; HttpErrorResponse has .error = parsed body
    const cast = error as { error?: unknown; message?: unknown };

    const fromMessage = this.normalizeMessagePart(cast.message);
    const fromError = this.normalizeMessagePart(cast.error);

    return [fromMessage, fromError].filter((part) => part.length > 0).join(' ');
  }

  private normalizeMessagePart(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }

    if (Array.isArray(value)) {
      return value
        .filter((part): part is string => typeof part === 'string')
        .join(' ');
    }

    if (value && typeof value === 'object') {
      const nested = value as { message?: unknown; error?: unknown };
      const nestedMessage = this.normalizeMessagePart(nested.message);
      const nestedError = this.normalizeMessagePart(nested.error);
      return [nestedMessage, nestedError]
        .filter((part) => part.length > 0)
        .join(' ');
    }

    return '';
  }
}
